import React, { useState, useEffect } from 'react';
import apiWithAuth from '../../services/apiWithAuth';
import { usePermissions } from '../../hooks/usePermissions';
import './NotificationPage.css';

// Keep the original styles object
const styles = {
    selectActions: {
        display: 'flex',
        alignItems: 'center',
        gap: '15px'
    },
    unselectButton: {
        padding: '4px 10px',
        backgroundColor: '#f0f0f0',
        border: '1px solid #ddd',
        borderRadius: '4px',
        cursor: 'pointer',
        fontSize: '14px'
    },
    disabledButton: {
        opacity: 0.5,
        cursor: 'not-allowed'
    }
};



const UserNotifications = () => {
    const permissions = usePermissions('Notifications');
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [notificationData, setNotificationData] = useState({
        title: '',
        body: ''
    });
    const [sendingNotification, setSendingNotification] = useState(false);
    const [notificationResponse, setNotificationResponse] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');

    // Fetch users with notification tokens
    useEffect(() => {
        fetchUsers();
    }, []);

    const fetchUsers = async () => {
        setLoading(true);
        setError(null);

        try {
            const response = await apiWithAuth.get('/notify/users-with-tokens');

            setUsers(response.data.data || []);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to fetch users with notification tokens');
            console.error('Error fetching users:', err);
        } finally {
            setLoading(false);
        }
    };

    // Handle user selection
    const toggleUserSelection = (userId) => {
        if (selectedUsers.includes(userId)) {
            setSelectedUsers(selectedUsers.filter(id => id !== userId));
        } else {
            setSelectedUsers([...selectedUsers, userId]);
        }
    };

    // Handle select all users
    const toggleSelectAll = () => {
        if (selectedUsers.length === filteredUsers.length) {
            setSelectedUsers([]);
        } else {
            setSelectedUsers(filteredUsers.map(user => user.id));
        }
    };

    // Handle notification input change
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setNotificationData({
            ...notificationData,
            [name]: value
        });
    };

    // Store unique roles for filter dropdown
    const [selectedRole, setSelectedRole] = useState('All');

    // Get unique roles from users
    const uniqueRoles = ['All', ...new Set(users.map(user => user.role))].filter(Boolean);

    // Filter users based on search term and role
    const filteredUsers = users.filter(user => {
        const matchesSearch =
            user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            user.phone.toLowerCase().includes(searchTerm.toLowerCase());

        const matchesRole = selectedRole === 'All' || user.role === selectedRole;

        return matchesSearch && matchesRole;
    });

    // Helper function to categorize selected users by role
    const categorizeSelectedUsers = () => {
        const selectedUserObjects = users.filter(user => selectedUsers.includes(user.id));
        const regularUsers = selectedUserObjects.filter(user => user.role === 'User');
        const shopOwners = selectedUserObjects.filter(user => user.role !== 'User');

        return {
            regularUsers: regularUsers.map(user => user.id),
            shopOwners: shopOwners.map(user => user.id),
            allRegularUsers: users.filter(user => user.role === 'User').map(user => user.id),
            allShopOwners: users.filter(user => user.role !== 'User').map(user => user.id)
        };
    };

    // Send notification to selected users
    const sendNotification = async () => {
        if (!notificationData.title || !notificationData.body) {
            setError('Title and body are required');
            return;
        }

        if (selectedUsers.length === 0) {
            setError('Please select at least one user');
            return;
        }

        setSendingNotification(true);
        setError(null);
        setNotificationResponse(null);

        try {
            const { regularUsers, shopOwners, allRegularUsers, allShopOwners } = categorizeSelectedUsers();
            let responses = [];

            // Send to regular users (User role) using /user-app/ endpoint
            if (regularUsers.length > 0) {
                let regularUserResponse;

                if (regularUsers.length === allRegularUsers.length && allRegularUsers.length > 0) {
                    // All regular users are selected, use broadcast API
                    regularUserResponse = await apiWithAuth.post('/user-app/notify/send/all', notificationData);
                } else {
                    // Only some regular users are selected
                    try {
                        regularUserResponse = await apiWithAuth.post('/user-app/notify/send/selected', {
                            ...notificationData,
                            userIds: regularUsers
                        });
                    } catch (endpointError) {
                        // Fallback to /send/all for regular users
                        console.warn('Failed to use /user-app/notify/send/selected, falling back to /user-app/notify/send/all:', endpointError);

                        regularUserResponse = await apiWithAuth.post('/user-app/notify/send/all', notificationData);

                        regularUserResponse.data.note = `Selected ${regularUsers.length} regular users, but sent to all regular users because the selected endpoint is not available.`;
                    }
                }

                responses.push({
                    type: 'Regular Users',
                    ...regularUserResponse.data,
                    selectedCount: regularUsers.length
                });
            }

            // Send to shop owners using regular endpoint
            if (shopOwners.length > 0) {
                let shopOwnerResponse;

                if (shopOwners.length === allShopOwners.length && allShopOwners.length > 0) {
                    // All shop owners are selected, use broadcast API
                    shopOwnerResponse = await apiWithAuth.post('/notify/send/all', notificationData);
                } else {
                    // Only some shop owners are selected
                    try {
                        shopOwnerResponse = await apiWithAuth.post('/notify/send/selected', {
                            ...notificationData,
                            userIds: shopOwners
                        });
                    } catch (endpointError) {
                        // Fallback to /send/all for shop owners
                        console.warn('Failed to use /notify/send/selected, falling back to /notify/send/all:', endpointError);

                        shopOwnerResponse = await apiWithAuth.post('/notify/send/all', notificationData);

                        shopOwnerResponse.data.note = `Selected ${shopOwners.length} shop owners, but sent to all shop owners because the selected endpoint is not available.`;
                    }
                }

                responses.push({
                    type: 'Shop Owners',
                    ...shopOwnerResponse.data,
                    selectedCount: shopOwners.length
                });
            }

            // Combine responses
            const combinedResponse = {
                success: true,
                responses: responses,
                totalSelectedUsers: selectedUsers.length,
                regularUsersSelected: regularUsers.length,
                shopOwnersSelected: shopOwners.length
            };

            setNotificationResponse(combinedResponse);

            // Reset form after successful submission
            setNotificationData({ title: '', body: '' });
        } catch (err) {
            setError(err.response?.data?.error || err.message);
        } finally {
            setSendingNotification(false);
        }
    };

    return (
        <div className="user-notification-container">
            <div className="notification-header">
                <h1 className="notification-title">Notifications</h1>
                <p className="notification-subtitle">
                    Send notifications to all or specific users with registered tokens
                </p>
            </div>

            {/* Search and user list */}
            <div className='flex-ofall'>
                <div className="users-section">
                    <div className="search-container">
                        <input
                            type="text"
                            placeholder="Search users by name or phone"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="search-input"
                        />
                        <select
                            className="role-filter"
                            value={selectedRole}
                            onChange={(e) => setSelectedRole(e.target.value)}
                        >
                            {uniqueRoles.map(role => (
                                <option key={role} value={role}>{role || 'No Role'}</option>
                            ))}
                        </select>
                        <button onClick={fetchUsers} className="refresh-button">
                            Refresh List
                        </button>
                    </div>

                    {loading ? (
                        <div className="loading-message">Loading users...</div>
                    ) : error ? (
                        <div className="error-message">{error}</div>
                    ) : (
                        <>
                            <div className="users-header">
                                <div className="user-count">
                                    {filteredUsers.length} users found with notification tokens
                                </div>
                                <div style={styles.selectActions}>
                                    <label className="select-all">
                                        <input
                                            type="checkbox"
                                            checked={selectedUsers.length === filteredUsers.length && filteredUsers.length > 0}
                                            onChange={toggleSelectAll}
                                        />
                                        Select All
                                    </label>
                                    <button
                                        style={{
                                            ...styles.unselectButton,
                                            ...(selectedUsers.length === 0 ? styles.disabledButton : {})
                                        }}
                                        onClick={() => setSelectedUsers([])}
                                        disabled={selectedUsers.length === 0}
                                    >
                                        Unselect All
                                    </button>
                                </div>
                            </div>

                            <div className="users-list">
                                {filteredUsers.length === 0 ? (
                                    <div className="no-users-message">No users found</div>
                                ) : (
                                    filteredUsers.map(user => (
                                        <div key={user.id} className="user-item">
                                            <div className="user-select">
                                                <input
                                                    type="checkbox"
                                                    checked={selectedUsers.includes(user.id)}
                                                    onChange={() => toggleUserSelection(user.id)}
                                                />
                                            </div>
                                            <div className="user-info">
                                                <div className="user-name">{user.name}</div>
                                                <div className="user-phone">{user.phone}</div>
                                                <div className="user-details">
                                                    <span className="user-role">{user.role || 'No Role'}</span>
                                                    <span
                                                        className="user-type"
                                                        style={{
                                                            backgroundColor:
                                                                user.role === 'Admin'
                                                                    ? '#DFF5DD' // Green background
                                                                    : user.role === 'User'
                                                                        ? '#001F4D'
                                                                        : '#FDF1D8',
                                                            color:
                                                                user.role === 'Admin'
                                                                    ? '#006400' // Dark green text
                                                                    : user.role === 'User'
                                                                        ? '#fff'
                                                                        : '#333'
                                                        }}
                                                    >
                                                        {user.role === 'User' && (
                                                            <img
                                                                src="https://admin-panel-kd.vercel.app/KD-icon.png"
                                                                alt="KaamDhanda icon"
                                                                style={{ width: '16px', height: '16px', marginRight: '5px', verticalAlign: 'middle' }}
                                                            />
                                                        )}

                                                        {(user.role === 'ShopOwner' ||
                                                            user.role === 'FieldBoy' ||
                                                            user.role === 'Blogger' ||
                                                            user.role === 'Individual' ||
                                                            user.role === 'ShopMember') && (
                                                                <img
                                                                    src="https://admin-panel-kd.vercel.app/KDP-icon.jpg"
                                                                    alt="KaamDhanda Partner icon"
                                                                    style={{ width: '16px', height: '16px', marginRight: '5px', verticalAlign: 'middle' }}
                                                                />
                                                            )}

                                                        {user.role === 'Admin'
                                                            ? 'YOU'
                                                            : user.role === 'User'
                                                                ? 'KaamDhanda'
                                                                : 'KaamDhanda Partner'}
                                                    </span>
                                                </div>
                                            </div>
                                            <div className="user-tokens">
                                                <span className="token-count">{user.tokenCount}</span>
                                                <span className="token-label">tokens</span>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        </>
                    )}
                </div>

                {/* Notification form */}
                <div className="notification-form">
                    <h2>Send Notification</h2>
                    <div className="selected-count">
                        {selectedUsers.length} users selected
                        {selectedUsers.length > 0 && (
                            <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                                {(() => {
                                    const { regularUsers, shopOwners } = categorizeSelectedUsers();
                                    const parts = [];
                                    if (regularUsers.length > 0) parts.push(`${regularUsers.length} KD users`);
                                    if (shopOwners.length > 0) parts.push(`${shopOwners.length} shop owners`);
                                    return parts.join(', ');
                                })()}
                            </div>
                        )}
                    </div>

                    <div className="form-group">
                        <label className="form-label">Notification Title</label>
                        <input
                            type="text"
                            name="title"
                            value={notificationData.title}
                            onChange={handleInputChange}
                            className="form-control"
                            placeholder="Enter notification title"
                            required
                        />
                    </div>

                    <div className="form-group">
                        <label className="form-label">Notification Body</label>
                        <textarea
                            name="body"
                            value={notificationData.body}
                            onChange={handleInputChange}
                            className="form-control form-textarea"
                            placeholder="Enter notification message"
                            required
                        />
                    </div>

                    <button
                        onClick={sendNotification}
                        disabled={sendingNotification || selectedUsers.length === 0}
                        className={`btn btn-primary ${sendingNotification || selectedUsers.length === 0 ? 'disabled' : ''}`}
                    >
                        {sendingNotification ? 'Sending...' : 'Send Notification'}
                    </button>
                </div>
            </div>

            {/* Response section */}
            {notificationResponse && (
                <div className="success-message">
                    <p>Notification sent successfully!</p>

                    {notificationResponse.responses && notificationResponse.responses.map((response, index) => (
                        <div key={index} className="response-group">
                            <h4>{response.type} Response</h4>
                            <p>Selected: {response.selectedCount} users</p>

                            {response.successCount !== undefined && response.failureCount !== undefined && (
                                <div className="stats-container">
                                    <div className="stat-card">
                                        <div className="stat-value success-stat">{response.successCount}</div>
                                        <div className="stat-label">Successful</div>
                                    </div>
                                    <div className="stat-card">
                                        <div className="stat-value error-stat">{response.failureCount}</div>
                                        <div className="stat-label">Failed</div>
                                    </div>
                                </div>
                            )}

                            <div className="response-container">
                                <div className="response-title">Response Details</div>
                                <pre className="response-content">{JSON.stringify(response, null, 2)}</pre>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default UserNotifications;